/**
 * SELVANE Collection Enhancement v13.0
 * ─────────────────────────────────────
 * Incremental enhancement layer for Agile theme's built-in collection grid.
 * Does NOT replace any existing Liquid files.
 * 
 * Features:
 *   1. Infinite Scroll (replaces pagination)
 *   2. Quick Add to Cart (hover overlay on product cards)
 *   3. Video auto-detection in product media
 *   4. Filter UX enhancement (mobile slide-out, active state)
 *   5. Product card hover video preview
 * 
 * Installation:
 *   Add to theme.liquid before </body>:
 *   {{ 'selvane-collection-enhance.js' | asset_url | script_tag }}
 * 
 * Configuration via data attributes on .section-main-collection:
 *   data-selvane-infinite-scroll="true|false"
 *   data-selvane-quick-add="true|false"
 *   data-selvane-video-preview="true|false"
 */

(function() {
  'use strict';

  /* ─── Guard: Only run on collection pages ─── */
  if (!document.body.classList.contains('template-collection')) return;

  const CONFIG = {
    infiniteScroll: true,
    quickAdd: true,
    videoPreview: true,
    loadMoreThreshold: 600, // px from bottom to trigger load
    quickAddDelay: 200,     // ms hover delay before showing quick add
  };

  /* Read overrides from DOM */
  const sectionEl = document.querySelector('.section-main-collection');
  if (sectionEl) {
    if (sectionEl.dataset.selvaneInfiniteScroll === 'false') CONFIG.infiniteScroll = false;
    if (sectionEl.dataset.selvaneQuickAdd === 'false') CONFIG.quickAdd = false;
    if (sectionEl.dataset.selvaneVideoPreview === 'false') CONFIG.videoPreview = false;
  }

  /* ═══════════════════════════════════════════
     1. INFINITE SCROLL
     ═══════════════════════════════════════════ */
  function initInfiniteScroll() {
    if (!CONFIG.infiniteScroll) return;

    const grid = document.querySelector('.collection .grid-cols');
    const paginationContainer = document.querySelector('.pagination') || 
                                 document.querySelector('.bls__pagination') ||
                                 document.querySelector('[class*="pagination"]');
    
    if (!grid) return;

    /* Hide existing pagination */
    if (paginationContainer) {
      paginationContainer.style.display = 'none';
    }

    /* Create loading indicator */
    const loader = document.createElement('div');
    loader.className = 'selvane-infinite-loader';
    loader.innerHTML = `
      <div class="selvane-loader-inner">
        <div class="selvane-loader-line"></div>
        <span class="selvane-loader-text">Loading</span>
      </div>
    `;
    loader.style.display = 'none';
    grid.parentElement.appendChild(loader);

    /* Create "end of collection" indicator */
    const endIndicator = document.createElement('div');
    endIndicator.className = 'selvane-collection-end';
    endIndicator.innerHTML = '<span>You have viewed all pieces</span>';
    endIndicator.style.display = 'none';
    grid.parentElement.appendChild(endIndicator);

    let isLoading = false;
    let currentPage = 1;
    let hasNextPage = true;

    /* Detect current page and next page URL */
    function getNextPageUrl() {
      const nextLink = document.querySelector('.pagination a[aria-label="Next"]') ||
                       document.querySelector('.pagination .next') ||
                       document.querySelector('link[rel="next"]');
      
      if (nextLink) {
        return nextLink.href || nextLink.getAttribute('href');
      }

      /* Construct URL manually */
      const url = new URL(window.location.href);
      currentPage++;
      url.searchParams.set('page', currentPage);
      return url.toString();
    }

    /* Fetch and append next page products */
    async function loadNextPage() {
      if (isLoading || !hasNextPage) return;
      isLoading = true;
      loader.style.display = 'flex';

      try {
        const nextUrl = getNextPageUrl();
        const response = await fetch(nextUrl);
        
        if (!response.ok) {
          hasNextPage = false;
          loader.style.display = 'none';
          endIndicator.style.display = 'flex';
          return;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProducts = doc.querySelectorAll('.collection .grid-cols > .product-wrapper');

        if (newProducts.length === 0) {
          hasNextPage = false;
          loader.style.display = 'none';
          endIndicator.style.display = 'flex';
          return;
        }

        /* Append new products with staggered animation */
        newProducts.forEach((product, index) => {
          product.style.opacity = '0';
          product.style.transform = 'translateY(20px)';
          grid.appendChild(product);
          
          requestAnimationFrame(() => {
            setTimeout(() => {
              product.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
              product.style.opacity = '1';
              product.style.transform = 'translateY(0)';
            }, index * 80);
          });
        });

        /* Check if there's another page */
        const nextPageLink = doc.querySelector('.pagination a[aria-label="Next"]') ||
                             doc.querySelector('.pagination .next') ||
                             doc.querySelector('link[rel="next"]');
        if (!nextPageLink) {
          hasNextPage = false;
        }

        /* Re-init quick add on new cards */
        if (CONFIG.quickAdd) {
          initQuickAddOnCards(newProducts);
        }

        /* Re-init video preview on new cards */
        if (CONFIG.videoPreview) {
          initVideoPreviewOnCards(newProducts);
        }

      } catch (error) {
        console.warn('[SELVANE] Infinite scroll error:', error);
        hasNextPage = false;
      } finally {
        isLoading = false;
        loader.style.display = 'none';
      }
    }

    /* Intersection Observer for scroll trigger */
    const sentinel = document.createElement('div');
    sentinel.className = 'selvane-scroll-sentinel';
    sentinel.style.height = '1px';
    grid.parentElement.appendChild(sentinel);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage) {
        loadNextPage();
      }
    }, {
      rootMargin: CONFIG.loadMoreThreshold + 'px'
    });

    observer.observe(sentinel);
  }


  /* ═══════════════════════════════════════════
     2. QUICK ADD TO CART
     ═══════════════════════════════════════════ */
  function initQuickAdd() {
    if (!CONFIG.quickAdd) return;
    const cards = document.querySelectorAll('.product-type__');
    initQuickAddOnCards(cards);
  }

  function initQuickAddOnCards(cards) {
    cards.forEach(card => {
      if (card.dataset.selvaneQuickAdd === 'initialized') return;
      card.dataset.selvaneQuickAdd = 'initialized';

      const inner = card.querySelector('.product__inner');
      if (!inner) return;

      /* Get product handle from the link */
      const productLink = card.querySelector('a.product-image');
      if (!productLink) return;
      const productUrl = productLink.getAttribute('href');

      /* Check if Agile's built-in quickview button exists */
      const existingQuickView = card.querySelector('button-quickview');
      
      /* Create Quick Add button (sits below quickview) */
      const quickAddBtn = document.createElement('button');
      quickAddBtn.className = 'selvane-quick-add-btn';
      quickAddBtn.innerHTML = `
        <span class="selvane-qa-text">Add to Bag</span>
        <span class="selvane-qa-loading" style="display:none;">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.5" stroke-dasharray="28" stroke-dashoffset="28" stroke-linecap="round">
              <animateTransform attributeName="transform" type="rotate" values="0 8 8;360 8 8" dur="0.8s" repeatCount="indefinite"/>
              <animate attributeName="stroke-dashoffset" values="28;0" dur="0.8s" fill="freeze"/>
            </circle>
          </svg>
        </span>
      `;
      
      quickAddBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await handleQuickAdd(productUrl, quickAddBtn);
      });

      inner.appendChild(quickAddBtn);
    });
  }

  async function handleQuickAdd(productUrl, btn) {
    const textEl = btn.querySelector('.selvane-qa-text');
    const loadingEl = btn.querySelector('.selvane-qa-loading');
    
    try {
      textEl.style.display = 'none';
      loadingEl.style.display = 'inline-flex';

      /* Fetch product data to get first available variant */
      const response = await fetch(productUrl + '.js');
      const product = await response.json();
      
      /* Find first available variant */
      const variant = product.variants.find(v => v.available) || product.variants[0];
      
      if (!variant) {
        textEl.textContent = 'Sold Out';
        textEl.style.display = 'inline';
        loadingEl.style.display = 'none';
        return;
      }

      /* Check if product has multiple options that need selection */
      const hasMultipleOptions = product.variants.length > 1 && 
                                  product.options.some(opt => opt.toLowerCase() !== 'title');
      
      if (hasMultipleOptions) {
        /* For products with size/color options, trigger Agile's quickview instead */
        const quickViewBtn = btn.closest('.product__inner').querySelector('button-quickview');
        if (quickViewBtn) {
          quickViewBtn.click();
          textEl.textContent = 'Add to Bag';
          textEl.style.display = 'inline';
          loadingEl.style.display = 'none';
          return;
        }
      }

      /* Add to cart via Shopify AJAX API */
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variant.id,
          quantity: 1
        })
      });

      if (addResponse.ok) {
        textEl.textContent = 'Added';
        textEl.style.display = 'inline';
        loadingEl.style.display = 'none';
        btn.classList.add('selvane-qa-success');

        /* Update cart count in header */
        const cartCountEl = document.querySelector('.cart-count-bubble span') ||
                            document.querySelector('[data-cart-count]');
        if (cartCountEl) {
          const currentCount = parseInt(cartCountEl.textContent) || 0;
          cartCountEl.textContent = currentCount + 1;
        }

        /* Trigger Shopify cart update event */
        document.dispatchEvent(new CustomEvent('cart:updated'));

        /* Open cart drawer if available */
        const cartDrawerTrigger = document.querySelector('[data-item="open-cart"]') ||
                                   document.querySelector('#cart-icon-bubble');
        if (cartDrawerTrigger) {
          setTimeout(() => cartDrawerTrigger.click(), 300);
        }

        /* Reset button after delay */
        setTimeout(() => {
          textEl.textContent = 'Add to Bag';
          btn.classList.remove('selvane-qa-success');
        }, 2000);
      } else {
        throw new Error('Add to cart failed');
      }

    } catch (error) {
      console.warn('[SELVANE] Quick add error:', error);
      textEl.textContent = 'Select Options';
      textEl.style.display = 'inline';
      loadingEl.style.display = 'none';
      
      /* Fallback: open quickview */
      const quickViewBtn = btn.closest('.product__inner').querySelector('button-quickview');
      if (quickViewBtn) {
        setTimeout(() => quickViewBtn.click(), 500);
      }

      setTimeout(() => {
        textEl.textContent = 'Add to Bag';
      }, 2000);
    }
  }


  /* ═══════════════════════════════════════════
     3. VIDEO PREVIEW ON PRODUCT CARDS
     ═══════════════════════════════════════════ */
  function initVideoPreview() {
    if (!CONFIG.videoPreview) return;
    const cards = document.querySelectorAll('.product-type__');
    initVideoPreviewOnCards(cards);
  }

  function initVideoPreviewOnCards(cards) {
    cards.forEach(card => {
      if (card.dataset.selvaneVideo === 'initialized') return;
      card.dataset.selvaneVideo = 'initialized';

      /* Check for video data in the product's variant JSON */
      const variantScript = card.querySelector('script.productVariantsQty');
      if (!variantScript) return;

      /* We'll fetch video on hover to avoid loading all videos upfront */
      const productLink = card.querySelector('a.product-image');
      if (!productLink) return;
      const productUrl = productLink.getAttribute('href');

      let videoFetched = false;
      let videoElement = null;

      card.addEventListener('mouseenter', async () => {
        if (videoFetched) {
          if (videoElement) {
            videoElement.style.opacity = '1';
            videoElement.play().catch(() => {});
          }
          return;
        }

        videoFetched = true;

        try {
          /* Fetch product page to find video */
          const response = await fetch(productUrl + '.json');
          const data = await response.json();
          const product = data.product;

          /* Find video in media */
          if (!product.media) return;
          const videoMedia = product.media.find(m => m.media_type === 'video');
          if (!videoMedia || !videoMedia.sources) return;

          /* Get highest quality source */
          const sources = videoMedia.sources.sort((a, b) => (b.width || 0) - (a.width || 0));
          const bestSource = sources[0];
          if (!bestSource || !bestSource.url) return;

          /* Create video element */
          videoElement = document.createElement('video');
          videoElement.className = 'selvane-card-video';
          videoElement.src = bestSource.url;
          videoElement.muted = true;
          videoElement.loop = true;
          videoElement.playsInline = true;
          videoElement.preload = 'metadata';
          videoElement.style.opacity = '0';

          const mediaContainer = card.querySelector('.product__media');
          if (mediaContainer) {
            mediaContainer.appendChild(videoElement);
            
            videoElement.addEventListener('canplay', () => {
              videoElement.style.opacity = '1';
              videoElement.play().catch(() => {});
            }, { once: true });
          }
        } catch (error) {
          console.warn('[SELVANE] Video preview error:', error);
        }
      });

      card.addEventListener('mouseleave', () => {
        if (videoElement) {
          videoElement.style.opacity = '0';
          videoElement.pause();
        }
      });
    });
  }


  /* ═══════════════════════════════════════════
     4. FILTER UX ENHANCEMENT
     ═══════════════════════════════════════════ */
  function initFilterEnhance() {
    /* Enhance filter summary/details with smooth animation */
    const filterDetails = document.querySelectorAll('.filter-product details');
    
    filterDetails.forEach(detail => {
      const summary = detail.querySelector('summary');
      if (!summary) return;

      /* Add chevron icon */
      if (!summary.querySelector('.selvane-chevron')) {
        const chevron = document.createElement('span');
        chevron.className = 'selvane-chevron';
        chevron.innerHTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.2">
          <path d="M3 4.5L6 7.5L9 4.5"/>
        </svg>`;
        summary.appendChild(chevron);
      }
    });

    /* Mobile: Convert filter to slide-out panel */
    if (window.innerWidth <= 768) {
      const filterContainer = document.querySelector('.filter-product .filter-count');
      if (filterContainer) {
        /* Create mobile filter trigger */
        const mobileTrigger = document.createElement('button');
        mobileTrigger.className = 'selvane-mobile-filter-trigger';
        mobileTrigger.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.2">
            <path d="M2 4h12M4 8h8M6 12h4"/>
          </svg>
          <span>Filter & Sort</span>
        `;

        /* Create mobile filter overlay */
        const overlay = document.createElement('div');
        overlay.className = 'selvane-mobile-filter-overlay';
        overlay.innerHTML = `
          <div class="selvane-mobile-filter-panel">
            <div class="selvane-mobile-filter-header">
              <span>Filter & Sort</span>
              <button class="selvane-mobile-filter-close">&times;</button>
            </div>
            <div class="selvane-mobile-filter-body"></div>
            <div class="selvane-mobile-filter-footer">
              <button class="selvane-mobile-filter-apply">View Results</button>
            </div>
          </div>
        `;

        document.body.appendChild(overlay);

        /* Move filter content into mobile panel */
        mobileTrigger.addEventListener('click', () => {
          const body = overlay.querySelector('.selvane-mobile-filter-body');
          body.innerHTML = filterContainer.innerHTML;
          overlay.classList.add('active');
          document.body.style.overflow = 'hidden';
        });

        overlay.querySelector('.selvane-mobile-filter-close').addEventListener('click', () => {
          overlay.classList.remove('active');
          document.body.style.overflow = '';
        });

        overlay.querySelector('.selvane-mobile-filter-apply').addEventListener('click', () => {
          overlay.classList.remove('active');
          document.body.style.overflow = '';
        });

        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            overlay.classList.remove('active');
            document.body.style.overflow = '';
          }
        });

        /* Insert mobile trigger before grid */
        const gridContainer = document.querySelector('.product-grid-container');
        if (gridContainer) {
          gridContainer.parentElement.insertBefore(mobileTrigger, gridContainer);
        }

        /* Hide desktop filter on mobile */
        filterContainer.classList.add('selvane-desktop-only');
      }
    }
  }


  /* ═══════════════════════════════════════════
     5. PRODUCT COUNT & SCROLL PROGRESS
     ═══════════════════════════════════════════ */
  function initScrollProgress() {
    const grid = document.querySelector('.collection .grid-cols');
    if (!grid) return;

    /* Create subtle progress indicator */
    const progressBar = document.createElement('div');
    progressBar.className = 'selvane-scroll-progress';
    progressBar.innerHTML = '<div class="selvane-scroll-progress-fill"></div>';
    
    const sectionMain = document.querySelector('.section-main-collection');
    if (sectionMain) {
      sectionMain.style.position = 'relative';
      sectionMain.appendChild(progressBar);
    }

    const fill = progressBar.querySelector('.selvane-scroll-progress-fill');

    window.addEventListener('scroll', () => {
      if (!sectionMain) return;
      const rect = sectionMain.getBoundingClientRect();
      const sectionHeight = sectionMain.offsetHeight;
      const scrolled = -rect.top;
      const progress = Math.min(Math.max(scrolled / (sectionHeight - window.innerHeight), 0), 1);
      fill.style.width = (progress * 100) + '%';
    }, { passive: true });
  }


  /* ═══════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════ */
  function init() {
    initInfiniteScroll();
    initQuickAdd();
    initVideoPreview();
    initFilterEnhance();
    initScrollProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
